import React from 'react'

const _chId = () => 'tnc-' + Math.floor(Math.random() * 1e9)

// Inject shimmer/pulse keyframes once.
if (typeof document !== 'undefined' && !document.getElementById('tn-charts-kf')) {
  const s = document.createElement('style')
  s.id = 'tn-charts-kf'
  s.textContent = `
    @keyframes tn-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    @keyframes tn-pulse  { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
  `
  document.head.appendChild(s)
}

// ─── Sparkline ──────────────────────────────────────────────────────
export interface SparklineProps {
  data: number[]
  width?: number
  height?: number
  color?: string
  fill?: boolean
}

export function Sparkline({ data, width = 120, height = 32, color = 'var(--brand-gold)', fill = true }: SparklineProps) {
  if (!data || !data.length) return null
  const min = Math.min(...data), max = Math.max(...data), span = max - min || 1
  const stepX = width / (data.length - 1)
  const pts = data.map((v, i): [number, number] => [i * stepX, height - ((v - min) / span) * (height - 4) - 2])
  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ')
  const id = _chId()
  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {fill && <path d={path + ` L${width},${height} L0,${height} Z`} fill={`url(#${id})`}/>}
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.5" fill={color}/>
    </svg>
  )
}

// ─── AreaChart ──────────────────────────────────────────────────────
export interface AreaChartProps {
  data: number[]
  width?: number
  height?: number
  color?: string
  showGrid?: boolean
  showAxis?: boolean
  labels?: string[]
  suffix?: string
}

export function AreaChart({ data, width = 320, height = 120, color = 'var(--brand-gold)', showGrid = true, showAxis = true, labels, suffix = '' }: AreaChartProps) {
  const max = Math.max(...data) * 1.15, span = max || 1
  const pad = { l: showAxis ? 28 : 4, r: 4, t: 6, b: showAxis ? 18 : 4 }
  const innerW = width - pad.l - pad.r, innerH = height - pad.t - pad.b
  const stepX = innerW / (data.length - 1)
  const pts = data.map((v, i): [number, number] => [pad.l + i * stepX, pad.t + innerH - (v / span) * innerH])
  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ')
  const id = _chId()
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {showGrid && [0.25, 0.5, 0.75].map((g, i) => (
        <line key={i} x1={pad.l} x2={pad.l + innerW} y1={pad.t + innerH * g} y2={pad.t + innerH * g} stroke="var(--border)" strokeWidth="1"/>
      ))}
      {showAxis && (
        <>
          <text x={pad.l - 6} y={pad.t + 6} textAnchor="end" style={{ font: '500 9px var(--font-mono)', fill: 'var(--text-dim)' }}>
            {Math.round(max).toLocaleString()}{suffix}
          </text>
          <text x={pad.l - 6} y={pad.t + innerH} textAnchor="end" style={{ font: '500 9px var(--font-mono)', fill: 'var(--text-dim)' }}>0</text>
        </>
      )}
      <path d={path + ` L${pad.l + innerW},${pad.t + innerH} L${pad.l},${pad.t + innerH} Z`} fill={`url(#${id})`}/>
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      {pts.length <= 16 && pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill="var(--bg-surface)" stroke={color} strokeWidth="1.4"/>
      ))}
      {labels && showAxis && labels.map((l, i) => (
        <text key={i} x={pad.l + i * stepX} y={height - 4} textAnchor="middle" style={{ font: '500 9px var(--font-mono)', fill: 'var(--text-dim)' }}>{l}</text>
      ))}
    </svg>
  )
}

// ─── BulletBar ──────────────────────────────────────────────────────
export interface BulletBarProps {
  actual: number
  target: number
  max?: number
  height?: number
  width?: number | string
  color?: string
  showLabels?: boolean
}

export function BulletBar({ actual, target, max, height = 14, width = '100%', color = 'var(--brand-gold)', showLabels = true }: BulletBarProps) {
  const maxV = max || target * 1.4
  const w80 = (target * 0.8) / maxV * 100
  const w110 = (target * 1.1) / maxV * 100
  const wTarget = (target / maxV) * 100
  const wActual = Math.min(100, (actual / maxV) * 100)
  const overage = actual > target
  return (
    <div style={{ width, position: 'relative', height: height + (showLabels ? 14 : 0) }}>
      <div style={{ position: 'relative', height, background: 'var(--bg-inset)', borderRadius: height / 2, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${w80}%`, background: 'var(--st-error-soft)' }}/>
        <div style={{ position: 'absolute', left: `${w80}%`, top: 0, bottom: 0, width: `${w110 - w80}%`, background: 'var(--st-success-soft)' }}/>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: `${wActual}%`,
          background: overage ? `linear-gradient(90deg, ${color} 0%, ${color} 80%, var(--st-warning) 100%)` : color,
          borderRadius: height / 2,
        }}/>
        <div style={{ position: 'absolute', left: `${wTarget}%`, top: -2, bottom: -2, width: 2, background: 'var(--text-primary)', borderRadius: 1 }}/>
      </div>
      {showLabels && (
        <div style={{
          position: 'relative', display: 'flex', justifyContent: 'space-between',
          font: '500 9.5px var(--font-mono)', color: 'var(--text-dim)', marginTop: 4, letterSpacing: 0.06,
        }}>
          <span>0</span>
          <span style={{ position: 'absolute', left: `${wTarget}%`, transform: 'translateX(-50%)', color: 'var(--text-muted)' }}>target {target}</span>
          <span>{Math.round(maxV)}</span>
        </div>
      )}
    </div>
  )
}

// ─── Gauge ──────────────────────────────────────────────────────────
export interface GaugeProps {
  value: number
  max?: number
  size?: number
  color?: string
  label?: string
  sub?: string
}

export function Gauge({ value, max = 100, size = 110, color = 'var(--brand-gold)', label, sub }: GaugeProps) {
  const r = (size - 16) / 2, cx = size / 2, cy = size - 8
  const circ = Math.PI * r, pct = Math.min(1, value / max)
  const arcPath = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
      <svg width={size} height={size / 2 + 4}>
        <path d={arcPath} fill="none" stroke="var(--bg-elevated)" strokeWidth="9" strokeLinecap="round"/>
        <path d={arcPath} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}/>
      </svg>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center' }}>
        <div style={{ font: `700 22px/1 var(--font-sans)`, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>
          {value}{label && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 2 }}>{label}</span>}
        </div>
        {sub && <div style={{ font: '500 10px var(--font-mono)', color: 'var(--text-dim)', marginTop: 3, letterSpacing: 0.06 }}>{sub}</div>}
      </div>
    </div>
  )
}

// ─── StackedBar ─────────────────────────────────────────────────────
export interface StackedBarSegment { value: number; color: string; label?: string }

export interface StackedBarProps {
  segments: StackedBarSegment[]
  width?: number | string
  height?: number
  gap?: number
}

export function StackedBar({ segments, width = '100%', height = 10, gap = 2 }: StackedBarProps) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1
  return (
    <div style={{ width, height, display: 'flex', gap, background: 'var(--bg-inset)', borderRadius: height / 2, overflow: 'hidden' }}>
      {segments.map((s, i) => (
        <div key={i} title={s.label} style={{ flex: s.value / total, background: s.color, height: '100%' }}/>
      ))}
    </div>
  )
}

// ─── ShimmerBar ─────────────────────────────────────────────────────
export interface ShimmerBarProps { width?: number | string; height?: number; radius?: number }

export function ShimmerBar({ width = '100%', height = 10, radius = 4 }: ShimmerBarProps) {
  return (
    <div style={{
      width, height, borderRadius: radius,
      background: 'linear-gradient(90deg, var(--bg-elevated), var(--bg-surface-hi, var(--bg-elevated)), var(--bg-elevated))',
      backgroundSize: '200% 100%', animation: 'tn-shimmer 1.6s linear infinite',
    }}/>
  )
}

// ─── LiveDot ────────────────────────────────────────────────────────
export interface LiveDotProps { color?: string; size?: number }

export function LiveDot({ color = 'var(--st-error)', size = 7 }: LiveDotProps) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
      <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: color, animation: 'tn-pulse 1.6s ease-in-out infinite' }}/>
    </span>
  )
}

// ─── ProjectCrest ────────────────────────────────────────────────────
export interface ProjectCrestProps { initials?: string; color?: string; size?: number; radius?: number }

export function ProjectCrest({ initials = 'LR', color = 'var(--proj-cedar)', size = 22, radius = 5 }: ProjectCrestProps) {
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, background: color,
      color: '#FFF', display: 'grid', placeItems: 'center',
      font: `700 ${size * 0.45}px/1 var(--font-sans)`, letterSpacing: '-0.01em', flexShrink: 0,
    }}>{initials}</div>
  )
}
